/**
 * useDashboardMode — Persistent dashboard view mode
 *
 * Stores 'business' or 'everything' in localStorage.
 * Syncs across components via a custom event.
 */

import { useState, useEffect, useCallback } from 'react';

export type DashboardMode = 'business' | 'everything';

const STORAGE_KEY = 'tp-dashboard-mode';
const EVENT_NAME = 'dashboard-mode-change';

function getStoredMode(): DashboardMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'everything') return 'everything';
  } catch { /* localStorage unavailable */ }
  return 'business';
}

export function useDashboardMode() {
  const [mode, setMode] = useState<DashboardMode>(getStoredMode);

  // Listen for mode changes from other components
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<DashboardMode>).detail;
      if (detail === 'business' || detail === 'everything') {
        setMode(detail);
      }
    };
    window.addEventListener(EVENT_NAME, handler);
    return () => window.removeEventListener(EVENT_NAME, handler);
  }, []);

  const setModeAndPersist = useCallback((newMode: DashboardMode) => {
    setMode(newMode);
    try { localStorage.setItem(STORAGE_KEY, newMode); } catch { /* noop */ }
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: newMode }));
  }, []);

  return { mode, setMode: setModeAndPersist };
}

export default useDashboardMode;
